import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { aboutInfo, faqs } from "@/mocks/articles";
import { useState, useRef } from "react";

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<{ type: "success" | "error" | null; message: string }>({ type: null, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: aboutInfo.brand_name,
    url: window.location.origin,
    description: aboutInfo.brand_intro,
    email: aboutInfo.contact.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: aboutInfo.contact.address,
      addressLocality: "北京",
      addressRegion: "朝阳区",
      addressCountry: "CN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: aboutInfo.contact.phone,
      email: aboutInfo.contact.email,
      contactType: "customer service",
      availableLanguage: "Chinese",
      hoursAvailable: aboutInfo.contact.work_hours,
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]} />
            </div>

            <section className="mb-16">
              <div className="max-w-3xl mx-auto text-center">
                <span className="px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4 inline-block">
                  关于我们
                </span>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mt-4 mb-6">
                  还原事实真相，传递真实声音
                </h1>
                <p className="text-base text-foreground-600 leading-relaxed">
                  {aboutInfo.brand_intro}
                </p>
              </div>
            </section>

            <section className="mb-16">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center bg-background-100 rounded-lg p-6">
                    <div className="text-3xl md:text-4xl font-bold text-primary-600 font-heading mb-2">{stat.value}</div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
                  我们的使命
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed text-center mb-6">
                  {aboutInfo.mission}
                </p>
                <p className="text-sm text-foreground-500 leading-relaxed text-center">
                  {aboutInfo.team}
                </p>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                发展历程
              </h2>
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-0.5"></div>
                  <div className="space-y-8">
                    {aboutInfo.milestones.map((milestone, index) => (
                      <div key={index} className={`relative flex items-start gap-6 ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                        <div className="flex-shrink-0 relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center mt-1 md:mx-auto md:absolute md:left-1/2 md:-translate-x-1/2">
                          <span className="w-3 h-3 rounded-full bg-background-50"></span>
                        </div>
                        <div className={`flex-1 bg-background-100 rounded-lg p-4 ${index % 2 === 0 ? "md:mr-[50%] md:pr-8" : "md:ml-[50%] md:pl-8"} md:text-right`}>
                          <span className="text-xs font-semibold text-primary-600 font-label">{milestone.year}</span>
                          <p className="text-sm text-foreground-700 mt-1">{milestone.event}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {aboutInfo.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-background-100 rounded-lg p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500"><i className="ri-star-fill text-sm"></i></span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{testimonial.content}&rdquo;</p>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{testimonial.name}</p>
                      <p className="text-xs text-foreground-500">{testimonial.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                联系我们
              </h2>
              <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-4">联系方式</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="w-3 h-3 flex items-center justify-center text-primary-600"><i className="ri-mail-line text-xs"></i></span>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">邮箱</p>
                        <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="w-3 h-3 flex items-center justify-center text-primary-600"><i className="ri-phone-line text-xs"></i></span>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">电话</p>
                        <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="w-3 h-3 flex items-center justify-center text-primary-600"><i className="ri-map-pin-line text-xs"></i></span>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">地址</p>
                        <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="w-3 h-3 flex items-center justify-center text-primary-600"><i className="ri-time-line text-xs"></i></span>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">工作时间</p>
                        <p className="text-sm text-foreground-500">{aboutInfo.contact.work_hours}</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-4">给我们留言</h3>
                  <form
                    ref={formRef}
                    className="space-y-4"
                    data-readdy-form="true"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setSubmitting(true);
                      setFormStatus({ type: null, message: "" });
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                      if (honeypot) {
                        setFormStatus({ type: "success", message: "留言已发送，我们会尽快回复您！" });
                        setSubmitting(false);
                        return;
                      }
                      try {
                        const formData = new FormData(form);
                        formData.delete("phone_alt");
                        const response = await fetch("https://readdy.ai/api/form/d9qs5m4nlsngrm4lm54g", {
                          method: "POST",
                          headers: { "Content-Type": "application/x-www-form-urlencoded" },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const responseText = await response.text();
                        let parsed: Record<string, unknown> = {};
                        try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
                        if (response.ok && parsed?.code === "OK") {
                          setFormStatus({ type: "success", message: "留言已发送，我们会尽快回复您！" });
                          form.reset();
                        } else {
                          const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string || (parsed?.meta as Record<string, unknown>)?.detail as string || responseText || "提交失败，请稍后再试";
                          if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
                            setFormStatus({ type: "success", message: "留言已发送，我们会尽快回复您！" });
                            form.reset();
                          } else {
                            setFormStatus({ type: "error", message: serverMsg });
                          }
                        }
                      } catch {
                        setFormStatus({ type: "error", message: "网络错误，请稍后再试" });
                      }
                      setSubmitting(false);
                    }}
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <div className="anti-spam-field">
                      <label htmlFor="phone-alt-about">备用电话</label>
                      <input id="phone-alt-about" name="phone_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    {formStatus.type && (
                      <div className={`text-sm px-4 py-2 rounded-lg ${formStatus.type === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {formStatus.message}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full px-4 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 text-sm whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {submitting ? "发送中..." : "发送留言"}
                    </button>
                  </form>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="max-w-2xl mx-auto space-y-3">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-background-100 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</span>
                      <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-500 transition-transform duration-200 ${openFaq === index ? "rotate-180" : ""}`}>
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}